import { WarningRounded } from "@mui/icons-material";
import { Grid, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import React from "react";

const UNPUBLISH_CONFIRMATION_TEXT = "UNPUBLISH";

type UseUnpublishConfirmationArgs = {
    classifierWording?: string;
    onConfirmUnpublish: () => unknown | Promise<unknown>;
};

export function useUnpublishConfirmation(args: UseUnpublishConfirmationArgs) {
    const [isConfirmUnpublishOpen, setIsConfirmUnpublishOpen] =
        React.useState(false);
    const [confirmUnpublishText, setConfirmUnpublishText] = React.useState("");
    const [isConfirmUnpublishLoading, setIsConfirmUnpublishLoading] =
        React.useState(false);

    const classifierWording = args.classifierWording ?? "classifier";

    const openConfirmation = () => setIsConfirmUnpublishOpen(true);

    const confirmationDialog = (
        <AcxDialog
            isOpen={isConfirmUnpublishOpen}
            onClose={() => setIsConfirmUnpublishOpen(false)}
            title={<WarningRounded color="error" fontSize="large" />}
            dialogContentChildren={
                <Grid container direction="column" rowGap={2}>
                    <Grid container item direction="column" rowGap={1}>
                        <Grid item>
                            {classifierWording !== "model" && (
                                <Typography
                                    color="gray.700"
                                    fontWeight="bold"
                                    fontSize="20px"
                                    lineHeight="160%"
                                >
                                    Unpublishing this {classifierWording} will
                                    permanently remove all results
                                </Typography>
                            )}
                            {classifierWording === "model" && (
                                <Typography
                                    color="gray.700"
                                    fontWeight="bold"
                                    fontSize="20px"
                                    lineHeight="160%"
                                >
                                    Unpublishing this {classifierWording} will
                                    remove all results
                                </Typography>
                            )}
                        </Grid>
                        <Grid
                            item
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                            }}
                        >
                            {classifierWording !== "model" && (
                                <Typography color="gray.500">
                                    Unpublishing this {classifierWording} will
                                    permanently delete all results associated
                                    and require reprocessing to restore any lost
                                    insights.
                                </Typography>
                            )}
                            {classifierWording === "model" && (
                                <Typography color="gray.500">
                                    Unpublishing this {classifierWording} will
                                    remove all results and associated Signals
                                    Reports.
                                </Typography>
                            )}
                            <Typography color="gray.500">
                                This action is irreversible. Please confirm you
                                understand the impact before proceeding.
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item direction="column" rowGap={1}>
                        <Grid item>
                            <Typography color="gray.700">
                                To confirm, type{" "}
                                <b>{UNPUBLISH_CONFIRMATION_TEXT}</b> in the box
                                below:
                            </Typography>
                            <AcxMainTextField
                                id="unpublish-classifier"
                                value={confirmUnpublishText}
                                onChange={(event) => {
                                    setConfirmUnpublishText(event.target.value);
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            }
        >
            <AcxButton
                onClick={async () => {
                    setIsConfirmUnpublishLoading(true);
                    try {
                        await args.onConfirmUnpublish();
                        setIsConfirmUnpublishOpen(false);
                    } catch (error) {
                        console.error(error);
                    } finally {
                        setIsConfirmUnpublishLoading(false);
                    }
                }}
                disabled={confirmUnpublishText !== UNPUBLISH_CONFIRMATION_TEXT}
                loading={isConfirmUnpublishLoading}
            >
                Unpublish
            </AcxButton>
        </AcxDialog>
    );

    return {
        openConfirmation,
        confirmationDialog,
        isLoading: isConfirmUnpublishLoading,
    };
}

type UnpublishButtonProps = {
    classifierWording?: string;
    onConfirmUnpublish: () => unknown | Promise<unknown>;
} & React.ComponentPropsWithoutRef<typeof AcxButton>;

export function UnpublishButton(props: UnpublishButtonProps) {
    const { classifierWording, onConfirmUnpublish, ...buttonProps } = props;
    const { openConfirmation, confirmationDialog, isLoading } =
        useUnpublishConfirmation({ classifierWording, onConfirmUnpublish });

    return (
        <React.Fragment>
            <AcxButton
                color="white"
                {...buttonProps}
                children={props.children || "Unpublish"}
                onClick={openConfirmation}
                loading={isLoading || props.loading}
            />
            {confirmationDialog}
        </React.Fragment>
    );
}
